import React, { useState } from 'react';
import { TrendingUp, DollarSign, Users, ShoppingBag, Package, BarChart3, Share2, Download } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

function MarketIndia() {
  const [showShareMenu, setShowShareMenu] = useState(false);

  const generatePDF = async () => {
    const content = document.getElementById('market-report-content');
    if (!content) return;

    const canvas = await html2canvas(content, {
      scale: 2,
      useCORS: true,
      logging: false
    });
    
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const imgWidth = 210;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const pageHeight = 297;
    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save('India-Market-Report-Motif Unbound.pdf');
  };

  const shareReport = (platform) => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent('Check out this India Market Report from Motif Unbound');
    
    const shareUrls = {
      whatsapp: `https://wa.me/?text=${text}%20${url}`,
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
    setShowShareMenu(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <div id="market-report-content" className="max-w-6xl mx-auto px-6 py-16">
        <h1 className="text-5xl font-bold mb-6 text-[#000000]">
          India Market Overview
        </h1>
        
        <p className="text-xl text-gray-700 mb-12">
          The world's fastest-growing major economy with massive digital adoption, young demographics, and exploding consumer market.
        </p>

        <section className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] text-white p-6 rounded-xl">
            <DollarSign size={32} className="mb-3" />
            <div className="text-3xl font-bold mb-2">$1.48T</div>
            <div className="text-sm opacity-90">Total Retail Sales 2025</div>
          </div>
          <div className="bg-gradient-to-br from-[#FFD965] to-[#f5cc4d] text-[#000000] p-6 rounded-xl">
            <TrendingUp size={32} className="mb-3" />
            <div className="text-3xl font-bold mb-2">+9.2%</div>
            <div className="text-sm opacity-90">YoY Growth Rate</div>
          </div>
          <div className="bg-gradient-to-br from-[#38A3A5] to-[#000000] text-white p-6 rounded-xl">
            <ShoppingBag size={32} className="mb-3" />
            <div className="text-3xl font-bold mb-2">$298B</div>
            <div className="text-sm opacity-90">E-commerce Sales 2025</div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-[#000000]">2025 Retail Performance Highlights</h2>
          <div className="bg-gray-50 p-8 rounded-xl border border-gray-200">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#38A3A5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <BarChart3 size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-[#000000]">Digital Commerce Explosion</h3>
                  <p className="text-gray-700 mb-2">India's e-commerce market is growing at 25% annually, driven by smartphone penetration and digital payments adoption.</p>
                  <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                    <li>820 million internet users (58% of population)</li>
                    <li>E-commerce penetration at 20.1% of retail</li>
                    <li>UPI transactions reached 15.5 billion/month</li>
                    <li>Quick commerce growing 85% annually</li>
                  </ul>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#FFD965] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Users size={24} className="text-[#000000]" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-[#000000]">Young, Aspirational Consumers</h3>
                  <p className="text-gray-700 mb-2">With median age of 28 and rising disposable incomes, Indian consumers are driving premiumization across categories.</p>
                  <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                    <li>580 million millennials and Gen Z consumers</li>
                    <li>Middle class expected to reach 580 million by 2030</li>
                    <li>Premium segment growing 2x faster than mass market</li>
                    <li>Brand consciousness and quality focus increasing</li>
                  </ul>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#38A3A5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Package size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-[#000000]">Manufacturing Powerhouse</h3>
                  <p className="text-gray-700 mb-2">India's "Make in India" initiative and competitive manufacturing costs make it an attractive sourcing destination.</p>
                  <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                    <li>World's 5th largest manufacturing economy</li>
                    <li>Strong capabilities in textiles, electronics, pharmaceuticals</li>
                    <li>Government incentives for manufacturing and exports</li>
                    <li>Growing DTC brand ecosystem</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-[#000000]">Key Retail Categories in 2025</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-xl border-2 border-gray-200">
              <h3 className="text-xl font-bold mb-3 text-[#000000]">Fashion & Apparel</h3>
              <div className="text-2xl font-bold text-[#38A3A5] mb-2">$298B</div>
              <p className="text-gray-700 text-sm mb-3">Largest retail category with rapid premiumization</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Traditional and ethnic wear</li>
                <li>• Western fast fashion</li>
                <li>• Athleisure and sportswear</li>
                <li>• Sustainable fashion emerging</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-xl border-2 border-gray-200">
              <h3 className="text-xl font-bold mb-3 text-[#000000]">Consumer Electronics</h3>
              <div className="text-2xl font-bold text-[#38A3A5] mb-2">$186B</div>
              <p className="text-gray-700 text-sm mb-3">Smartphones and accessories dominate</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Smartphones (95% of category)</li>
                <li>• Wearables and hearables</li>
                <li>• Smart home devices</li>
                <li>• Gaming and entertainment</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-xl border-2 border-gray-200">
              <h3 className="text-xl font-bold mb-3 text-[#000000]">Beauty & Personal Care</h3>
              <div className="text-2xl font-bold text-[#38A3A5] mb-2">$89B</div>
              <p className="text-gray-700 text-sm mb-3">Fast-growing with strong DTC presence</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Skincare and cosmetics</li>
                <li>• Ayurvedic and natural products</li>
                <li>• Men's grooming expanding</li>
                <li>• Online-first brands thriving</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-xl border-2 border-gray-200">
              <h3 className="text-xl font-bold mb-3 text-[#000000]">Home & Furniture</h3>
              <div className="text-2xl font-bold text-[#38A3A5] mb-2">$72B</div>
              <p className="text-gray-700 text-sm mb-3">Rising home ownership driving growth</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Furniture and home decor</li>
                <li>• Home appliances</li>
                <li>• Kitchen and dining</li>
                <li>• Smart home products</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-[#000000]">Market Entry Considerations</h2>
          <div className="space-y-4">
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold mb-2">Tier 1, 2, and 3 Cities</h3>
              <p className="text-gray-700 text-sm">While metros offer affluence, Tier 2/3 cities represent 60% of e-commerce growth with 400+ million consumers.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold mb-2">Marketplace Dominance</h3>
              <p className="text-gray-700 text-sm">Amazon India, Flipkart, and quick commerce players dominate. Nykaa, Myntra lead in specialized categories.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold mb-2">Payment Preferences</h3>
              <p className="text-gray-700 text-sm">UPI, digital wallets, and COD essential. Buy Now Pay Later growing rapidly among younger consumers.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold mb-2">Social Commerce</h3>
              <p className="text-gray-700 text-sm">WhatsApp, Instagram, and live commerce driving sales. Influencer marketing highly effective for DTC brands.</p>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-[#000000]">Why Indian Manufacturers Choose Us</h2>
          <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-xl border-2 border-gray-200">
            <p className="text-gray-700 mb-6">We help Indian manufacturers transform into global DTC brands and expand into international markets:</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#FFD965] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-[#000000] font-bold">✓</span>
                </div>
                <span className="text-sm text-gray-700">DTC brand building and positioning for domestic and global markets</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#FFD965] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-[#000000] font-bold">✓</span>
                </div>
                <span className="text-sm text-gray-700">US, Canada, and LATAM market entry support</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#FFD965] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-[#000000] font-bold">✓</span>
                </div>
                <span className="text-sm text-gray-700">Nearshoring setup in Mexico for tariff-free US access</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#FFD965] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-[#000000] font-bold">✓</span>
                </div>
                <span className="text-sm text-gray-700">E-commerce launch and digital marketing expertise</span>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-gradient-to-r from-[#38A3A5] to-[#38A3A5] text-white p-8 rounded-2xl">
          <h2 className="text-2xl font-bold mb-4">Ready to Go Global from India?</h2>
          <p className="mb-6">Whether you're a manufacturer looking to build a DTC brand or an Indian brand ready to expand internationally, we provide the expertise and infrastructure you need.</p>
          <div className="flex flex-wrap gap-4">
            <a
              href="mailto:contact@motifunbound.com?subject=Schedule%20a%20Consult&body=Hi%20Motif%20Unbound%20team,%0D%0A%0D%0AI%27d%20like%20to%20schedule%20a%20consultation%20to%20discuss%20our%20market%20expansion.%20Please%20share%20available%20times.%0D%0A%0D%0AThank%20you."
              className="bg-[#FFD965] hover:bg-[#f5cc4d] text-[#000000] px-8 py-3 rounded-lg font-semibold transition-all inline-flex items-center gap-2"
            >
              Schedule a Consult
            </a>
            <button 
              onClick={generatePDF}
              className="bg-white/10 hover:bg-white/20 border-2 border-white text-white px-8 py-3 rounded-lg font-semibold transition-all inline-flex items-center gap-2"
            >
              <Download size={20} />
              Download Market Report
            </button>
            <div className="relative">
              <button 
                onClick={() => setShowShareMenu(!showShareMenu)}
                className="bg-white/10 hover:bg-white/20 border-2 border-white text-white px-8 py-3 rounded-lg font-semibold transition-all inline-flex items-center gap-2"
              >
                <Share2 size={20} />
                Share Report
              </button>
              {showShareMenu && (
                <div className="absolute top-full mt-2 bg-white rounded-lg shadow-xl p-3 min-w-[200px] z-10">
                  <button
                    onClick={() => shareReport('whatsapp')}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded text-gray-800 font-medium"
                  >
                    📱 WhatsApp
                  </button>
                  <button
                    onClick={() => shareReport('linkedin')}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded text-gray-800 font-medium"
                  >
                    💼 LinkedIn
                  </button>
                  <button
                    onClick={() => shareReport('twitter')}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded text-gray-800 font-medium"
                  >
                    🐦 Twitter
                  </button>
                  <button
                    onClick={() => shareReport('facebook')}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded text-gray-800 font-medium"
                  >
                    👥 Facebook
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default MarketIndia;
